import React, { useMemo } from 'react'
import { ReactComponent as CloseIcon } from '@assets/images/Icons/Close.svg'
import { ReactComponent as DownloadIcon } from '@assets/images/Icons/Download.svg'
import { ReactComponent as MoveToIcon } from '@assets/images/Icons/Move_to_folder_2.svg'
import { ReactComponent as FilesIcon } from '@assets/images/Icons/Files.svg'
import { ReactComponent as DeleteIcon } from '@assets/images/Icons/Delete1.svg'
import type {
  FileActionsAction,
  FileActionsDefaultActionId,
  FileActionsProps,
} from './FileActions.types'
import './FileActions.css'

type SvgIconComponent = React.FC<React.SVGProps<SVGSVGElement>>

const TintIcon: React.FC<{
  Icon: SvgIconComponent
  mode?: 'fill' | 'stroke' | 'mixed'
}> = ({ Icon, mode = 'fill' }) => (
  <Icon
    className="ds-file-actions__icon"
    aria-hidden="true"
    data-stroke={mode === 'stroke' ? 'true' : undefined}
    data-mixed={mode === 'mixed' ? 'true' : undefined}
  />
)

const DEFAULT_ICONS: Record<
  'download' | 'move' | 'copy' | 'delete',
  React.ReactNode
> = {
  download: <TintIcon Icon={DownloadIcon} mode="stroke" />,
  move: <TintIcon Icon={MoveToIcon} />,
  copy: <TintIcon Icon={FilesIcon} mode="stroke" />,
  delete: <TintIcon Icon={DeleteIcon} />,
}

const FileActions: React.FC<FileActionsProps> = ({
  selectedCount,
  selectedLabel = 'Selected',
  onClear,
  onDownload,
  onMove,
  onCopy,
  onDelete,
  actions: actionsProp,
  hiddenActions = [],
  darkMode = false,
  disabled = false,
  className = '',
}) => {
  const actions = useMemo((): FileActionsAction[] => {
    if (actionsProp) {
      return actionsProp
    }

    const defaults: FileActionsAction[] = [
      {
        id: 'download',
        label: 'Download',
        icon: DEFAULT_ICONS.download,
        onClick: onDownload,
      },
      {
        id: 'move',
        label: 'Move',
        icon: DEFAULT_ICONS.move,
        onClick: onMove,
      },
      {
        id: 'copy',
        label: 'Make a copy',
        icon: DEFAULT_ICONS.copy,
        onClick: onCopy,
      },
      {
        id: 'delete',
        label: 'Delete',
        icon: DEFAULT_ICONS.delete,
        onClick: onDelete,
        danger: true,
      },
    ]

    return defaults.filter(
      (action) =>
        !hiddenActions.includes(action.id as FileActionsDefaultActionId),
    )
  }, [actionsProp, hiddenActions, onCopy, onDelete, onDownload, onMove])

  const classNames = [
    'ds-file-actions',
    darkMode ? 'ds-file-actions--dark' : '',
    disabled ? 'ds-file-actions--disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={classNames}
      role="toolbar"
      aria-label="File actions"
      aria-disabled={disabled || undefined}
    >
      <div className="ds-file-actions__left">
        <button
          type="button"
          className="ds-file-actions__btn"
          aria-label="Clear selection"
          title="Clear selection"
          disabled={disabled}
          onClick={onClear}
        >
          <TintIcon Icon={CloseIcon} />
        </button>
        <p className="ds-file-actions__count">
          ({selectedCount}) {selectedLabel}
        </p>
      </div>

      <div className="ds-file-actions__right">
        {actions.map((action) => (
          <button
            key={action.id}
            type="button"
            className={[
              'ds-file-actions__btn',
              action.danger ? 'ds-file-actions__btn--danger' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label={action.label}
            title={action.label}
            disabled={disabled || action.disabled}
            onClick={action.onClick}
          >
            {action.icon ??
              (action.id in DEFAULT_ICONS
                ? DEFAULT_ICONS[action.id as keyof typeof DEFAULT_ICONS]
                : null)}
          </button>
        ))}
      </div>
    </div>
  )
}

export default FileActions
